import React from 'react';
import useThrottle from '@lib/hooks/useThrottle';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@nextui-org/react';
import ColorPicker, {
  type ColorPickerProps,
} from 'react-best-gradient-color-picker';
import { Tooltip } from 'react-tooltip';

type Props = {
  value: string;
  disableGradient?: boolean;
  disableOpacity?: boolean;
  showPopoverContent?: boolean;
};

export default function GradientColorPicker({
  disableGradient = false,
  disableOpacity = false,
  showPopoverContent = true,
  value,
  onChange,
}: Props & ColorPickerProps) {
  const {
    isOpen: isColorPickerOpen,
    onOpen: onColorPickerOpen,
    onClose: onColorPickerClose,
  } = useDisclosure();

  const handleChange = (color: string) => {
    onChange(color);
  };

  const throttledOnChange = useThrottle(handleChange, 200);

  return (
    <Popover
      classNames={{
        content: 'p-0',
        trigger: 'aria-expanded:opacity-100 aria-expanded:scale-100',
      }}
      color="warning"
      placement="bottom-end"
      isOpen={isColorPickerOpen}
      onClose={onColorPickerClose}
    >
      <PopoverTrigger>
        <div
          data-tooltip-id={`${!showPopoverContent && 'color-picker-tooltip'}`}
          data-tooltip-content={`${!showPopoverContent && 'This color cannot be edited. For customization, please choose a different style.'}`}
          onClick={onColorPickerOpen}
          className="size-8 rounded-lg border border-grey-200 dark:border-grey-800"
          style={{ background: value, position: 'relative' }}
        >
          {!showPopoverContent && <Tooltip id="color-picker-tooltip"
            place="top-end"
            style={{
              maxWidth: '300px',
              whiteSpace: 'normal',
              padding: '8px',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />}
        </div>
      </PopoverTrigger>
      {showPopoverContent && (
        <PopoverContent>
          <div className="bg-white p-2 rounded-lg max-h-[650px] overflow-auto border border-200 shadow-lg">
            <ColorPicker
              value={value}
              onChange={throttledOnChange}
              hideControls={disableGradient}
              hideOpacity={disableOpacity}
              className="!bg-white"
            />
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}
